import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common'; // Required for *ngIf, *ngFor
import { TableModule } from 'primeng/table';   // Required for p-table
import { TagModule } from 'primeng/tag';     // Required for p-tag
import { FormsModule } from '@angular/forms';
import { ViewChild } from '@angular/core';
import { Table } from 'primeng/table';
import { TranslateService } from '../../../../services/translate.service';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner'; // For p-progressSpinner if you add it
import { TooltipModule } from 'primeng/tooltip';
import { MessageService, ConfirmationService } from 'primeng/api'; // Import services for messages and confirmations
import { PhoneNumber } from '../../../../models/user.model';
import { MaintainAccountService } from '../maintain-account.service';

import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subject, takeUntil } from 'rxjs';
import { PhoneNumbersComponent } from '../../create-account/steps/phone-numbers/phone-numbers.component';

import { Pagination } from '../../../../services/pagination.abstruct';

@Component({
	selector: 'app-tab-phone-numbers',
	standalone: true,
	imports: [
		CommonModule,
		TableModule,
		FormsModule,
		TagModule,
		TooltipModule,
		ButtonModule,
		ProgressSpinnerModule
	],
	templateUrl: './tab-phone-numbers.component.html',
	styleUrls: ['./tab-phone-numbers.component.scss'],
	providers: [DialogService, MessageService]

})
export class TabPhoneNumbersComponent extends Pagination implements OnInit, OnDestroy {
	@Input() phoneNumbers!: any[];
	@Input() phoneNumbersLoading!: boolean;
	@Input() phoneNumbersError!: boolean;

	@Input() customerRecId!: number;


	@ViewChild('dt') dt!: Table;

	globalFilter: string = '';
	globalFilterFields: string[] = [];

	ref: DynamicDialogRef | undefined;
	private destroy$ = new Subject<void>();

	constructor(public translateServ: TranslateService,
		private messageService: MessageService,
		private confirmationService: ConfirmationService,
		private maintainAccountService: MaintainAccountService,
		private dialogService: DialogService) {super(); }

	ngOnInit(): void {
		if (this.phoneNumbers?.length > 0) {
			this.globalFilterFields = Object.keys(this.phoneNumbers[0]);
		}
	}

	ngOnDestroy(): void {
		if (this.ref) {
			this.ref.close();
		}
	}

	getPhoneNumberSeverity(isActive: number): string {
		return isActive === 1 ? 'success' : 'danger';
	}

	editPhoneNumbers(phoneNumber: PhoneNumber): void {
		this.ref = this.dialogService.open(PhoneNumbersComponent,
			{
				header: this.translateServ.dictionary['STEPPER_DIALOG_TITLE_EDIT'] || 'Edit Record',
				width: '70%',
				contentStyle: { overflow: 'auto' },
				baseZIndex: 10000,
				maximizable: true,
				data: {
					isDialog: true,
					isCreate: false,
					customerRecId: this.customerRecId,
					nameEntry: {
						id: phoneNumber.RecID.toString(),
						recId: phoneNumber.PhoneRecID,
						prefix: phoneNumber.Pref,
						number: phoneNumber.Phone_Without_Pref,
						description: phoneNumber.Descr,
						isActive: phoneNumber.IsActive,
						isApproved: phoneNumber.IsApproved,
					}
				}
			});

		this.ref.onClose.pipe(takeUntil(this.destroy$)).subscribe((result) => {
			if (result) {
				this.messageService.add({
					severity: 'success',
					summary: this.translateServ.dictionary['SUCCESS'] || 'Success',
					detail: this.translateServ.dictionary['MAINT_UPDATE_SUCCESS_MSG'] || 'Record updated successfully.',
					life: 3000
				});
				this.reloadPhoneNumbersForUser();
			}
		});
	}


	/**
	 * Function to update the status of an additional name via an API call.
	 * It takes the RecID and the status as a number (1 for active, 0 for inactive).
	 * @param recId The RecID of the additional name to update.
	 * @param newStatus The new status (1 for active, 0 for inactive).
	 */
	updatePhoneStatus(source: boolean, recId: number, newStatus: 0 | 1): void {
		// Calling the API service to update the status
		this.maintainAccountService.updateFunctions(source, this.constructor.name.substring(1), recId, newStatus).subscribe({
			next: (response: any) => {
				// Check if the API response indicates a successful update
				if (response && response[0]?.RecID && response[0].RecID !== -1) {
					const index = this.phoneNumbers?.findIndex(name => name.RecID === recId);
					if (this.phoneNumbers && index !== undefined && index > -1) {
						// Correctly updating the 'IsActive' or 'IsApproved' property
						if (source) {
							this.phoneNumbers[index].IsActive = newStatus;
						}
						else {
							this.phoneNumbers[index].IsApproved = newStatus;
						}

						const statusText = newStatus === 1 ? 'Active' : 'Inactive';
						this.messageService.add({
							severity: 'success',
							summary: this.translateServ.dictionary['STATUS_UPDATED'] || 'Status Updated',
							// Correctly using the 'Street' property for the message
							detail: `Phone number "${this.phoneNumbers[index].Phone}" is now ${statusText}.`,
							life: 3000
						});
					}
				} else {
					this.messageService.add({
						severity: 'error',
						summary: this.translateServ.dictionary['ERROR'] || 'Error',
						detail: this.translateServ.dictionary['STATUS_UPDATE_FAILED'] || 'Failed to update status. Record not found or invalid response.',
						life: 5000
					});
				}
			},
			error: (err: any) => {
				console.error('API Error:', err);
				this.messageService.add({
					severity: 'error',
					summary: this.translateServ.dictionary['ERROR'] || 'Error',
					detail: this.translateServ.dictionary['STATUS_UPDATE_FAILED'] || 'Failed to update status. Please try again.',
					life: 5000
				});
			}
		});
	}



	togglePhoneNumberStatus(phoneNumber: PhoneNumber, action: boolean): void {
		const newStatus = action ? (phoneNumber.IsActive === 1 ? 0 : 1) : (phoneNumber.IsApproved === 1 ? 0 : 1);
		const actionText = newStatus === 1 ?
			(this.translateServ.dictionary['ACTIVATE'] || 'activate') :
			(this.translateServ.dictionary['DEACTIVATE'] || 'deactivate');

		this.confirmationService.confirm({
			message: `${this.translateServ.dictionary['CONFIRM_CHANGE_STATUS'] || 'Are you sure you want to'} ${actionText} ${phoneNumber.Phone}?`,
			header: this.translateServ.dictionary['CONFIRM_ACTION'] || 'Confirm Action',
			icon: 'pi pi-exclamation-triangle',
			acceptLabel: this.translateServ.dictionary['YES'] || 'Yes',
			rejectLabel: this.translateServ.dictionary['NO'] || 'No',
			accept: () => {
				this.updatePhoneStatus(action, phoneNumber.RecID, newStatus);
			},
			reject: () => {
				this.messageService.add({
					severity: 'info',
					summary: this.translateServ.dictionary['CANCELLED'] || 'Cancelled',
					detail: this.translateServ.dictionary['ACTION_CANCELLED'] || 'Action cancelled.',
					life: 3000
				});
			}
		});
	}

	createNewRecord(): void {
		this.ref = this.dialogService.open(PhoneNumbersComponent, {
			header: this.translateServ.dictionary['STEPPER_DIALOG_TITLE'] || 'Create New Record',
			width: '70%',
			contentStyle: { overflow: 'auto' },
			baseZIndex: 10000,
			maximizable: true,
			data: {
				isDialog: true,
				isCreate: true,
				customerRecId: this.customerRecId // Pass the ID here
			}
		});

		// Subscribe to the dialog's onClose event
		this.ref.onClose.pipe(takeUntil(this.destroy$)).subscribe((result) => {
			// Check if the dialog was closed with a successful action (e.g., a "save" button click)
			// The AdditionalNamesComponent would need to return a value like 'true' upon success
			// to trigger this.
			if (result) {
				this.messageService.add({
					severity: 'success',
					summary: this.translateServ.dictionary['SUCCESS'] || 'Success',
					detail: this.translateServ.dictionary['MAINT_CREATE_SUCCESS_MSG'] || 'Record created successfully.',
					life: 3000
				});
				this.reloadPhoneNumbersForUser();
			}
		});
	}


	// Confirms and deletes a record
	confirmDelete(phoneNumber: PhoneNumber): void {
		this.confirmationService.confirm({
			message: `${this.translateServ.dictionary['MAINT_DELETE_CONFIRM_MSG'] || 'Are you sure you want to delete this record?'} (ID: ${phoneNumber.RecID})`,
			header: this.translateServ.dictionary['MAINT_DELETE_CONFIRM_HEADER'] || 'Confirm Deletion',
			icon: 'pi pi-exclamation-triangle',
			acceptLabel: this.translateServ.dictionary['CONFIRM_DELETE'] || 'Delete',
			rejectLabel: this.translateServ.dictionary['CONFIRM_CANCEL'] || 'Cancel',
			accept: () => {
				this.maintainAccountService.deleteRecord(this.constructor.name.substring(1), phoneNumber.RecID)
					.pipe(takeUntil(this.destroy$))
					.subscribe({
						next: (response) => {
							this.messageService.add({
								severity: 'success',
								summary: this.translateServ.dictionary['SUCCESS'] || 'Success',
								detail: this.translateServ.dictionary['MAINT_DELETE_SUCCESS_MSG'] || 'Record deleted successfully.',
								life: 3000,
							});
							this.reloadPhoneNumbersForUser();
						},
						error: (error) => {
							console.error('Failed to delete record:', error);
							this.messageService.add({
								severity: 'error',
								summary: this.translateServ.dictionary['ERROR'] || 'Error',
								detail: this.translateServ.dictionary['MAINT_DELETE_FAILED_MSG'] || 'Failed to delete record.',
								life: 5000,
							});
						}
					});
			},
			reject: () => {
				// The user clicked "Cancel", so we can just close the dialog.
			}
		});
	}


	private reloadPhoneNumbersForUser(): void {
		// Call the service method to get the updated data
		const index =  this.maintainAccountService.getMappingIndex("phoneNumbers");
		const instance = this.maintainAccountService.getUserDataMappings();
		this.maintainAccountService.getDatas(this.customerRecId, instance[index].apiName, instance[index].special)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (data) => {
					this.phoneNumbers.splice(0, this.phoneNumbers.length, ...data);
					this.phoneNumbersLoading = false;
				},
				error: (error) => {
					console.error('Failed to reload phone numbers:', error);
					this.phoneNumbersError = true;
					this.phoneNumbersLoading = false;
					this.messageService.add({
						severity: 'error',
						summary: this.translateServ.dictionary['ERROR'] || 'Error',
						detail: this.translateServ.dictionary['MAINT_LOAD_FAILED_MSG_PHONE'] || 'Failed to load phone numbers.',
						life: 5000
					});
				}
			});
	}

	applyGlobalFilter(): void {
		this.dt.filterGlobal(this.globalFilter, 'contains');
	}
}